import { DynamicSettings } from "Components/admin/DynamicSettings";
import { useMobileSearchboxModal } from "../mobile-searchbox-modal/useMobileSearchboxModal";
import { html } from "lit-html";
import classNames from "classnames";
import i18next from "i18next";
import { useState, useMemo } from "Shared/haunted/CustomHooks";
import { City, Country } from "Components/searchbox/searchTab/flights/RoutePickerModel";
import { stationSelectorSearchIcon } from "Components/searchbox/searchTab/flights/station-selector/newFlightSvgs";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { stationCommonTemplates } from "./stationCommonTemplates";
import { ALL_COUNTRY_CODE, stationUtils } from "./stationUtils";
import {
	allStationsPlaceholder,
	findCountryInModel,
	hasRelevantDestinations,
	highlightTextTemplate,
	stationCountryArrowTemplate,
} from "ComponentHelpers/StationSelectorHelper";

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
	journeyCountries: Country[];
	journeyIndex: number;
	journeyType: "origin" | "destination";
	onClick: (city: City, e: MouseEvent) => void;
	onInput: () => void;
}

export const useMobileSearchboxStationSelectorModal = (props: Props) => {
	const [openCountryCodes, setOpenCountryCodes] = useState<Set<string>>(new Set());
	const [filterExpression, setFilterExpression] = useState<string>("");
	const [defaultCountryCode, setDefaultCountryCode] = useState<string>("");

	const { getFilteredModel, getDefaultCountryCodeFromSettings } = stationUtils();
	const { noResultTemplate } = stationCommonTemplates();

	const filteredModel = useMemo(() => {
		if (!props.journeyCountries?.length) return [];
		return getFilteredModel(props.journeyCountries, filterExpression) || [];
	}, [props.journeyCountries, filterExpression]);

	const orderedCountries = useMemo(() => {
		if (!filteredModel?.length) return [allStationsPlaceholder];

		const defaultCountry = filteredModel.find((country) => country.code === defaultCountryCode);
		const otherCountries = filteredModel.filter((country) => country.code !== defaultCountryCode);

		return defaultCountry
			? [allStationsPlaceholder, defaultCountry, ...otherCountries]
			: [allStationsPlaceholder, ...filteredModel];
	}, [defaultCountryCode, filteredModel]);

	const flatCityList = useMemo(
		() => filteredModel?.flatMap((country) => country.cities as City[]) ?? [],
		[filteredModel]
	);

	const setAsDefault = (code: string) => {
		setDefaultCountryCode(code);
		setOpenCountryCodes(new Set([code]));
	};

	const initializeDestinationCountry = (defaultCountryCodeFromSettings: string) => {
		const defaultCountryInModel = findCountryInModel(filteredModel, defaultCountryCodeFromSettings);

		if (hasRelevantDestinations(defaultCountryInModel, props.currentSearchManager.currentSearch.originCity)) {
			setAsDefault(defaultCountryInModel.code);
			return;
		}

		const cultureBasedCountry = findCountryInModel(filteredModel, props.culture.substring(3, 5));

		if (
			cultureBasedCountry &&
			hasRelevantDestinations(cultureBasedCountry, props.currentSearchManager.currentSearch.originCity)
		) {
			setAsDefault(cultureBasedCountry.code);
		} else {
			setAsDefault(allStationsPlaceholder.code);
		}
	};

	const initializeOriginCountry = (defaultCountryCodeFromSettings: string) => {
		const cultureBasedCountry = findCountryInModel(filteredModel, defaultCountryCodeFromSettings);

		if (cultureBasedCountry) {
			setAsDefault(cultureBasedCountry.code);
		}
	};

	const init = () => {
		// DEVNOTE: Default country selection logic:
		// • For origin modal: Use DynamicSettings default country or culture-based fallback
		// • For destination modal with selected origin:
		//   - First try: DynamicSettings default country (if has relevant destinations from origin)
		//   - Second try: Culture-based country (if has relevant destinations from origin)
		//   - Fallback: "All destinations" (if no relevant destinations found)

		if (!props.dynamicSettings || !filteredModel.length || defaultCountryCode) return;

		const defaultCountryCodeFromSettings = getDefaultCountryCodeFromSettings(
			props.journeyIndex,
			props.culture,
			props.dynamicSettings
		);

		if (props.journeyType === "destination" && props.currentSearchManager.currentSearch.originCity) {
			initializeDestinationCountry(defaultCountryCodeFromSettings);
		} else {
			initializeOriginCountry(defaultCountryCodeFromSettings);
		}
	};

	const toggleCountry = (e: MouseEvent, countryCode: string) => {
		e.preventDefault();
		e.stopPropagation();

		setOpenCountryCodes((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(countryCode)) {
				newSet.delete(countryCode);
			} else {
				newSet.clear();
				newSet.add(countryCode);
			}
			return newSet;
		});
	};

	const handleInput = (e: KeyboardEvent) => {
		props.onInput();
		setFilterExpression((e.target as HTMLInputElement).value);
	};

	const handleCityClick = (city: City, e: MouseEvent) => {
		setFilterExpression("");
		props.onClick(city, e);
	};

	// TEMPLATES

	const cityTemplate = (city: City) => html`
		<li
			class="group flex w-full cursor-pointer justify-between gap-x-4 py-1 font-body text-sm leading-6"
			@click=${(e: MouseEvent) => handleCityClick(city, e)}
		>
			<span class="text-[#444444]"> ${city.name} </span>
			<span class="text-n-light-gray"> ${city.code} </span>
		</li>
	`;

	const searchResultCityTemplate = (city: City) => {
		const displayName = `${city.name} (${city.code})`;
		const highlightedName = highlightTextTemplate(displayName, filterExpression);

		return html`
			<li
				class="group flex w-full cursor-pointer py-2 font-body text-sm leading-6 hover:bg-[#f0f0f0]"
				@click=${(e: MouseEvent) => handleCityClick(city, e)}
			>
				<span class="text-[#444444]"> ${highlightedName} </span>
			</li>
		`;
	};

	const allCountriesGroupTemplate = (country: Country) => html`
		<div class="mb-3 border-b border-solid border-[#e0e0e0]">
			<div class="mb-1 font-body text-base font-bold leading-5 text-n-cyan">${country.name}</div>
			<ul class="mb-1">
				${country.cities.map(cityTemplate)}
			</ul>
		</div>
	`;

	const countryDropdownTemplate = (country: Country) => {
		const isOpen = openCountryCodes.has(country.code);
		const isAllDestinations = country.code === ALL_COUNTRY_CODE;

		return html`
			<div class="overflow-hidden">
				<div
					class="flex h-8 cursor-pointer items-center justify-between py-1"
					@click=${(e: MouseEvent) => toggleCountry(e, country.code)}
				>
					<span class="whitespace-nowrap font-body text-base font-bold leading-5 text-n-blue">
						${country.name}
					</span>
					${stationCountryArrowTemplate(country.code, openCountryCodes)}
				</div>
				<div
					class=${classNames("transition-all duration-300 ease-in-out", {
						"max-h-0 opacity-0": !isOpen,
						"max-h-[800px] opacity-100": isOpen && !isAllDestinations,
						"max-h-[2800px] opacity-100": isOpen && isAllDestinations,
					})}
				>
					${isAllDestinations
						? html`
								<div class="mt-2 overflow-y-auto px-2">
									${filteredModel.map(allCountriesGroupTemplate)}
								</div>
							`
						: html`
								<ul class="mt-2 overflow-y-auto px-2">
									${country.cities.map(cityTemplate)}
								</ul>
							`}
				</div>
			</div>
		`;
	};

	const searchResultsTemplate = () =>
		flatCityList?.length
			? html`
					<div class="flex flex-col rounded-lg bg-white">
						<ul class="px-2">
							${flatCityList.map(searchResultCityTemplate)}
						</ul>
					</div>
				`
			: noResultTemplate();

	const groupedStationsTemplate = () =>
		orderedCountries?.length
			? html`
					<div class="flex flex-col rounded-lg bg-white">
						${orderedCountries.map(countryDropdownTemplate)}
					</div>
				`
			: noResultTemplate();

	const stationsTemplate = () => (filterExpression ? searchResultsTemplate() : groupedStationsTemplate());

	const inputValue =
		(props.journeyType === "origin"
			? props.currentSearchManager.currentSearch.originCity?.name
			: props.currentSearchManager.currentSearch.destinationCity?.name) || "";

	const inputTemplate = () => html`
		<div class="relative">
			${stationSelectorSearchIcon}
			<input
				class=${classNames(
					"w-full cursor-pointer appearance-none rounded-lg border-none bg-[#F7F7F7] text-n-blue pr-4 pl-10 py-3 text-base outline-hidden",
					"placeholder:text-base placeholder:text-n-light-gray",
					"focus:cursor-text",
					{
						"text-n-blue": inputValue || filterExpression,
					}
				)}
				placeholder=${i18next.t("Busca por ciudad o aeropuerto")}
				value=${filterExpression || inputValue || ""}
				.value=${filterExpression || inputValue || ""}
				@input=${handleInput}
			/>
		</div>
	`;

	const contentTemplate = () => html`
		<div class="flex h-full flex-col gap-4 overflow-hidden">
			${inputTemplate()}
			<div class="flex-1 overflow-y-auto pb-4">${stationsTemplate()}</div>
		</div>
	`;

	const modal = useMobileSearchboxModal({
		title: props.journeyType === "origin" ? i18next.t("Elige el origen") : i18next.t("Elige el destino"),
		content: contentTemplate(),
		onClose: () => {
			setFilterExpression("");
		},
	});

	useEffectWithoutCleanup(init, [
		modal.isOpen,
		filteredModel,
		props.dynamicSettings,
		defaultCountryCode,
		props.currentSearchManager.currentSearch.originCity,
	]);

	return modal;
};
